import { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FileText,
  Upload,
  CheckSquare,
  Square,
  AlertCircle,
  Loader2,
  X,
  BookOpen,
  Calendar,
  Users,
  Check,
  SkipForward,
  AlertTriangle,
  RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { api } from '@/lib/api';
import type {
  ImportSyncResult,
  ImportAsyncResult,
  ImportDryRunResult,
  JobStatusResult,
} from '@/lib/api';
import { useAuthStore } from '@/store/authStore';
import {
  parseReferences,
  validateImportFile,
} from '@/features/sources/citations';
import type {
  ParsedImportEntry,
  ImportSession,
  ImportResult,
} from '@/features/sources/types';

interface DryRunEntry {
  index: number;
  title: string | null;
  status: 'new' | 'duplicate';
  reason?: string;
}

interface DryRunResult {
  entries: DryRunEntry[];
  new_count: number;
  duplicate_count: number;
}

// ---- Format configurations ----

interface ImportFormatConfig {
  format: 'ris' | 'bib' | 'pubmed';
  displayName: string;
  fileJoinSeparator: string;
  accept: Record<string, string[]>;
  dropLabel: string;
  dropActiveLabel: string;
  multiFileLabel: string;
}

const FORMAT_CONFIGS: Record<string, ImportFormatConfig> = {
  ris: {
    format: 'ris',
    displayName: 'RIS',
    fileJoinSeparator: '\n',
    accept: {
      'text/plain': ['.ris'],
      'application/x-research-info-systems': ['.ris'],
    },
    dropLabel: 'Drag & drop .ris file(s) here',
    dropActiveLabel: 'Drop your RIS file(s) here',
    multiFileLabel: 'RIS files',
  },
  bib: {
    format: 'bib',
    displayName: 'BibTeX',
    fileJoinSeparator: '\n',
    accept: {
      'text/plain': ['.bib', '.bibtex'],
      'application/x-bibtex': ['.bib', '.bibtex'],
      'text/x-bibtex': ['.bib', '.bibtex'],
    },
    dropLabel: 'Drag & drop .bib file(s) here',
    dropActiveLabel: 'Drop your BibTeX file(s) here',
    multiFileLabel: 'BibTeX files',
  },
  pubmed: {
    format: 'pubmed',
    displayName: 'PubMed',
    fileJoinSeparator: '\n\n',
    accept: {
      'text/plain': ['.txt', '.nbib'],
    },
    dropLabel: 'Drag & drop .txt or .nbib file(s) here',
    dropActiveLabel: 'Drop your PubMed file(s) here',
    multiFileLabel: 'PubMed files',
  },
};

const MAX_RAW_LENGTH = 500;
const POLL_INTERVAL = 2000;

// ---- Helpers ----

function isAsyncResult(data: unknown): data is ImportAsyncResult {
  return !!data && typeof data === 'object' && 'job_id' in data;
}

function isDryRunResult(data: unknown): data is ImportDryRunResult {
  return !!data && typeof data === 'object' && 'entries' in data && 'new_count' in data;
}

// ---- Progress stage labels ----

const STAGE_LABELS: Record<string, string> = {
  dedup: 'Checking for duplicates',
  inserting: 'Importing references',
  folders: 'Organizing into folder',
};

// ---- Component ----

interface GenericImporterProps {
  format: 'ris' | 'bib' | 'pubmed';
  folderId: string | null;
  onCancel?: () => void;
  onFolderCreated?: (folderId: string) => void;
}

export function GenericImporter({
  format,
  folderId,
  onCancel,
  onFolderCreated,
}: GenericImporterProps) {
  const config = FORMAT_CONFIGS[format]!;
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();

  const [session, setSession] = useState<ImportSession | null>(null);
  const [importProgress, setImportProgress] = useState<{
    stage: string;
    current: number;
    total: number;
    percent: number;
  } | null>(null);
  const [lastResult, setLastResult] = useState<ImportResult | null>(null);
  const [dryRunResult, setDryRunResult] = useState<DryRunResult | null>(null);
  const [isDryRunning, setIsDryRunning] = useState(false);
  const [createImportFolder, setCreateImportFolder] = useState(!folderId);

  // Polling ref for async jobs
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // ---- File parsing ----

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    const fileName =
      acceptedFiles.length === 1
        ? acceptedFiles[0]!.name
        : `${acceptedFiles.length} ${config.multiFileLabel}`;

    setSession({
      format: config.format,
      file_name: fileName,
      entries: [],
      total_count: 0,
      selected_count: 0,
      duplicate_count: 0,
      status: 'parsing',
      error: null,
    });

    Promise.all(
      acceptedFiles.map(
        (file) =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target?.result as string);
            reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
            reader.readAsText(file);
          })
      )
    )
      .then(async (contents) => {
        const content = contents.join(config.fileJoinSeparator);

        const validationError = validateImportFile(content, config.format);
        if (validationError) {
          setSession((prev) =>
            prev ? { ...prev, status: 'failed', error: validationError } : null
          );
          return;
        }

        const entries = parseReferences(content, config.format);

        setSession({
          format: config.format,
          file_name: fileName,
          entries,
          total_count: entries.length,
          selected_count: entries.filter((e) => e.selected).length,
          duplicate_count: 0,
          status: 'ready',
          error: null,
        });

        // Auto dry-run: single POST (server handles dedup via RPC)
        setIsDryRunning(true);
        try {
          const preparedEntries = entries.map((e) => ({
            metadata: e.metadata,
            raw: e.raw.length > MAX_RAW_LENGTH ? e.raw.slice(0, MAX_RAW_LENGTH) + '...' : e.raw,
          }));

          const data = await api.importReferences({
            entries: preparedEntries,
            folder_id: folderId || undefined,
            dry_run: true,
            format: config.format,
          }).catch((err) => { console.error('[GenericImporter] dry-run failed:', err); return null; });

          if (data && isDryRunResult(data)) {
            const drResult: DryRunResult = {
              entries: data.entries,
              new_count: data.new_count,
              duplicate_count: data.duplicate_count,
            };
            setDryRunResult(drResult);

            // Mark duplicate entries as deselected
            if (drResult.duplicate_count > 0) {
              const dupIndices = new Set(
                drResult.entries.filter((e) => e.status === 'duplicate').map((e) => e.index)
              );
              const updatedEntries = entries.map((entry, idx) => ({
                ...entry,
                selected: !dupIndices.has(idx),
              }));
              setSession((prev) =>
                prev ? {
                  ...prev,
                  entries: updatedEntries,
                  selected_count: updatedEntries.filter((e) => e.selected).length,
                  duplicate_count: drResult.duplicate_count,
                } : null
              );
            }
          }
        } catch {
          // Dry-run failure is non-blocking
        } finally {
          setIsDryRunning(false);
        }
      })
      .catch((err) => {
        const message =
          err instanceof Error ? err.message : `Failed to parse ${config.displayName} file`;
        setSession((prev) =>
          prev ? { ...prev, status: 'failed', error: message } : null
        );
      });
  }, [config, folderId]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: config.accept,
    disabled: session?.status === 'importing',
  });

  // ---- Selection ----

  function toggleEntry(id: string) {
    if (!session) return;
    const updated = session.entries.map((entry) =>
      entry.id === id ? { ...entry, selected: !entry.selected } : entry
    );
    setSession({
      ...session,
      entries: updated,
      selected_count: updated.filter((e) => e.selected).length,
    });
  }

  function toggleAll() {
    if (!session) return;
    const allSelected = session.entries.every((e) => e.selected);
    const updated = session.entries.map((e) => ({
      ...e,
      selected: !allSelected,
    }));
    setSession({
      ...session,
      entries: updated,
      selected_count: updated.filter((e) => e.selected).length,
    });
  }

  // ---- Job polling ----

  function startPolling(jobId: string) {
    setImportProgress({ stage: 'dedup', current: 0, total: 0, percent: 0 });

    pollRef.current = setInterval(async () => {
      try {
        const status = await api.getJobStatus(jobId, 'import');
        handleJobStatus(status);
      } catch {
        // Polling error — keep trying
      }
    }, POLL_INTERVAL);
  }

  function handleJobStatus(status: JobStatusResult) {
    // Update progress from job
    if (status.progress && typeof status.progress === 'object') {
      const { stage, current, total } = status.progress;
      const percent = total > 0 ? Math.round((current / total) * 100) : 0;
      setImportProgress({
        stage,
        current,
        total,
        percent,
      });
    }

    if (status.status === 'completed') {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }

      const result = status.result;
      if (result) {
        const importResult: ImportResult = {
          imported: result.imported,
          skipped_duplicates: result.skipped_duplicates,
          failed: result.failed,
          errors: result.errors,
        };
        setLastResult(importResult);
        setSession((prev) => prev ? { ...prev, status: 'completed' } : null);

        queryClient.invalidateQueries({ queryKey: ['sources'] });

        if (importResult.imported > 0) {
          toast.success(`Imported ${importResult.imported} reference${importResult.imported > 1 ? 's' : ''}`);
        }
        if (importResult.skipped_duplicates > 0) {
          toast.info(`Skipped ${importResult.skipped_duplicates} duplicate${importResult.skipped_duplicates > 1 ? 's' : ''}`);
        }
        if (importResult.failed > 0) {
          toast.error(`Failed to import ${importResult.failed} reference(s)`);
        }
      }
    } else if (status.status === 'failed') {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      toast.error(status.error || 'Import job failed');
      setSession((prev) => prev ? { ...prev, status: 'failed', error: status.error || 'Import job failed' } : null);
    }
  }

  // ---- Import mutation ----

  const importMutation = useMutation({
    mutationFn: async (
      { entries, targetFolderId }: { entries: ParsedImportEntry[]; targetFolderId: string | null }
    ): Promise<ImportResult> => {
      if (!user) throw new Error('Not authenticated');
      if (!session) throw new Error('No import session');

      const selected = entries.filter((e) => e.selected);

      const preparedEntries = selected.map((e) => ({
        metadata: e.metadata,
        raw: e.raw.length > MAX_RAW_LENGTH ? e.raw.slice(0, MAX_RAW_LENGTH) + '...' : e.raw,
      }));

      // Single POST with all entries
      const data = await api.importReferences({
        entries: preparedEntries,
        folder_id: targetFolderId || undefined,
        format: session.format,
      });

      // Async path: server returned a job_id → poll for progress
      if (isAsyncResult(data)) {
        startPolling(data.job_id);
        // Return partial result — the polling will handle completion
        return {
          imported: 0,
          skipped_duplicates: data.skipped_duplicates,
          failed: 0,
          errors: [],
        };
      }

      // Sync path: server returned the result directly
      const result = data as ImportSyncResult;
      return {
        imported: result.imported,
        skipped_duplicates: result.skipped_duplicates,
        failed: result.failed,
        errors: result.errors,
      };
    },
    onSuccess: (result) => {
      // For async imports, don't finalize here — polling handles it
      if (pollRef.current) return;

      queryClient.invalidateQueries({ queryKey: ['sources'] });
      setSession((prev) =>
        prev ? { ...prev, status: 'completed' } : null
      );
      setLastResult(result);

      if (result.imported > 0) {
        toast.success(
          `Imported ${result.imported} reference${result.imported > 1 ? 's' : ''}`
        );
      }
      if (result.skipped_duplicates > 0) {
        toast.info(
          `Skipped ${result.skipped_duplicates} duplicate${result.skipped_duplicates > 1 ? 's' : ''}`
        );
      }
      if (result.failed > 0) {
        toast.error(`Failed to import ${result.failed} reference(s)`);
      }
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : 'Import failed'
      );
      setSession((prev) =>
        prev ? { ...prev, status: 'failed', error: 'Import failed' } : null
      );
    },
  });

  async function handleImport() {
    if (!session || !user) return;
    setSession({ ...session, status: 'importing' });
    setImportProgress({ stage: 'dedup', current: 0, total: 0, percent: 0 });

    // Auto-create import folder if requested
    let targetFolderId = folderId;
    if (createImportFolder && !folderId) {
      try {
        const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        const folderName = `Import — ${session.file_name} — ${date}`;
        const { data: folder, error } = await supabase
          .from('folders')
          .insert({
            user_id: user.id,
            name: folderName,
            folder_type: 'import',
            description: null,
            parent_id: null,
            color: null,
          })
          .select()
          .single();

        if (!error && folder) {
          targetFolderId = folder.id;
          queryClient.invalidateQueries({ queryKey: ['folders'] });
          onFolderCreated?.(folder.id);
        }
      } catch {
        // Folder creation failure is non-blocking
      }
    }

    importMutation.mutate({ entries: session.entries, targetFolderId });
  }

  function handleReset() {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setSession(null);
    setImportProgress(null);
    setLastResult(null);
    setDryRunResult(null);
  }

  // ---- Render: drop zone ----

  if (!session) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="h-5 w-5" />
            Import {config.displayName} File
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div
            {...getRootProps()}
            className={cn(
              'flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-8 text-center transition-colors cursor-pointer',
              isDragActive
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'
            )}
          >
            <input {...getInputProps()} />
            <Upload className="h-8 w-8 text-slate-400" />
            <div>
              <p className="font-medium text-slate-700">
                {isDragActive ? config.dropActiveLabel : config.dropLabel}
              </p>
              <p className="mt-1 text-sm text-slate-500">
                or click to browse
              </p>
            </div>
          </div>
          {onCancel && (
            <div className="mt-4 flex justify-end">
              <Button variant="ghost" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // ---- Render: error state ----

  if (session.status === 'failed') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg text-rose-600">
            <AlertCircle className="h-5 w-5" />
            Import Error
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600">{session.error}</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleReset}>
              Try Again
            </Button>
            {onCancel && (
              <Button variant="ghost" onClick={onCancel}>
                Cancel
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // ---- Render: parsing state ----

  if (session.status === 'parsing') {
    return (
      <Card>
        <CardContent className="flex items-center justify-center gap-3 py-12">
          <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
          <span className="text-sm text-slate-600">
            Parsing {session.file_name}...
          </span>
        </CardContent>
      </Card>
    );
  }

  // ---- Render: completed state ----

  if (session.status === 'completed' && lastResult) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Check className="h-5 w-5 text-emerald-600" />
            Import Complete
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            {lastResult.imported > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-emerald-600" />
                <span className="text-slate-700">
                  <span className="font-medium">{lastResult.imported}</span> imported
                </span>
              </div>
            )}
            {lastResult.skipped_duplicates > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <SkipForward className="h-4 w-4 text-amber-500" />
                <span className="text-slate-700">
                  <span className="font-medium">{lastResult.skipped_duplicates}</span> skipped (duplicates)
                </span>
              </div>
            )}
            {lastResult.failed > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <AlertTriangle className="h-4 w-4 text-rose-500" />
                <span className="text-slate-700">
                  <span className="font-medium">{lastResult.failed}</span> failed
                </span>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleReset}>
              <RotateCcw className="mr-2 h-4 w-4" />
              Import another file
            </Button>
            {onCancel && (
              <Button variant="ghost" onClick={onCancel}>
                Close
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // ---- Render: entries list ----

  const allSelected = session.entries.every((e) => e.selected);
  const isImporting = session.status === 'importing';

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="h-5 w-5" />
            {session.file_name}
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleReset}
            disabled={isImporting}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center gap-3 text-sm text-slate-500">
          <span>{session.total_count} references found</span>
          <Separator orientation="vertical" className="h-4" />
          <span>{session.selected_count} selected</span>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Select all toggle */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={toggleAll}
            disabled={isImporting}
            className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-indigo-600 disabled:opacity-50"
          >
            {allSelected ? (
              <CheckSquare className="h-4 w-4 text-indigo-600" />
            ) : (
              <Square className="h-4 w-4" />
            )}
            {allSelected ? 'Deselect all' : 'Select all'}
          </button>
          <Badge variant="secondary">
            {session.selected_count} / {session.total_count}
          </Badge>
        </div>

        {/* Dry-run summary */}
        {isDryRunning && (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Checking for duplicates...
          </div>
        )}
        {dryRunResult && !isDryRunning && dryRunResult.duplicate_count > 0 && (
          <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {dryRunResult.duplicate_count} duplicate{dryRunResult.duplicate_count > 1 ? 's' : ''} detected — deselected by default. You can re-select them to force import.
          </div>
        )}

        {/* Entries list */}
        <div className="h-[360px] overflow-y-auto rounded-md border">
          <div className="divide-y">
            {session.entries.map((entry, idx) => (
              <EntryRow
                key={entry.id}
                entry={entry}
                disabled={isImporting}
                onToggle={() => toggleEntry(entry.id)}
                dupStatus={dryRunResult?.entries.find((e) => e.index === idx)}
              />
            ))}
          </div>
        </div>

        {/* Import progress */}
        {isImporting && importProgress && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-slate-600">
                <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
                {STAGE_LABELS[importProgress.stage] || 'Processing'}
                {importProgress.total > 0 && `... ${importProgress.current} / ${importProgress.total}`}
              </span>
              {importProgress.percent > 0 && (
                <span className="font-medium">{importProgress.percent}%</span>
              )}
            </div>
            <Progress value={importProgress.percent} />
          </div>
        )}
        {isImporting && !importProgress && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
              Starting import...
            </div>
            <Progress />
          </div>
        )}

        {/* Auto-create folder option */}
        {!folderId && (
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={createImportFolder}
              onChange={(e) => setCreateImportFolder(e.target.checked)}
              disabled={isImporting}
              className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            Create import folder automatically
          </label>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button
              variant="ghost"
              onClick={onCancel}
              disabled={isImporting}
            >
              Cancel
            </Button>
          )}
          <Button
            onClick={handleImport}
            disabled={session.selected_count === 0 || isImporting}
          >
            {isImporting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Importing...
              </>
            ) : (
              `Import ${session.selected_count} reference${session.selected_count !== 1 ? 's' : ''}`
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// =========================================
// Entry row sub-component
// =========================================

interface EntryRowProps {
  entry: ParsedImportEntry;
  disabled: boolean;
  onToggle: () => void;
  dupStatus?: DryRunEntry;
}

function EntryRow({ entry, disabled, onToggle, dupStatus }: EntryRowProps) {
  const { metadata } = entry;
  const isDuplicate = dupStatus?.status === 'duplicate';

  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      className={cn(
        'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors',
        isDuplicate && !entry.selected && 'bg-amber-50/50',
        entry.selected ? 'bg-indigo-50/50' : 'hover:bg-slate-50',
        disabled && 'opacity-60 cursor-not-allowed'
      )}
    >
      {/* Selection indicator */}
      <div className="mt-0.5 shrink-0">
        {entry.selected ? (
          <CheckSquare className="h-4 w-4 text-indigo-600" />
        ) : (
          <Square className="h-4 w-4 text-slate-400" />
        )}
      </div>

      {/* Entry info */}
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-slate-900 line-clamp-2 flex-1">
            {metadata.title ?? 'Untitled'}
          </p>
          {dupStatus && (
            <Badge
              variant={isDuplicate ? 'secondary' : 'outline'}
              className={cn(
                'shrink-0 text-[10px]',
                isDuplicate ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-600 border-emerald-200'
              )}
            >
              {isDuplicate ? 'Duplicate' : 'New'}
            </Badge>
          )}
        </div>

        {isDuplicate && dupStatus?.reason && (
          <p className="text-[11px] text-amber-600">{dupStatus.reason}</p>
        )}

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
          {metadata.authors.length > 0 && (
            <span className="flex items-center gap-1">
              <Users className="h-3 w-3" />
              {metadata.authors.length <= 2
                ? metadata.authors.join(', ')
                : `${metadata.authors[0]} et al.`}
            </span>
          )}
          {metadata.publication_year && (
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {metadata.publication_year}
            </span>
          )}
          {metadata.journal && (
            <span className="flex items-center gap-1">
              <BookOpen className="h-3 w-3" />
              <span className="truncate max-w-[200px]">
                {metadata.journal}
              </span>
            </span>
          )}
        </div>

        {metadata.doi && (
          <p className="text-xs font-mono text-slate-400 truncate">
            DOI: {metadata.doi}
          </p>
        )}
      </div>
    </button>
  );
}
